import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";

type TabType = "buyer" | "dropshipper" | "influencer";

const tabs: { key: TabType; label: string; icon: string }[] = [
  { key: "buyer", label: "Bulk Buyer", icon: "ri-building-4-line" },
  { key: "dropshipper", label: "Dropshipper", icon: "ri-truck-line" },
  { key: "influencer", label: "Influencer", icon: "ri-star-line" },
];

const buyerSteps = [
  {
    num: "01", icon: "ri-search-line", title: "Search & Discover",
    desc: "Browse 5M+ verified factory products using our advanced search. Filter by country, category, price range, and verification status.",
    tip: "Use the 'Verified Only' filter to see only gold-badge factories.",
    color: "bg-amber-500",
  },
  {
    num: "02", icon: "ri-test-tube-line", title: "Order a Sample",
    desc: "Before committing to bulk, order 1–10 units at the sample price. Inspect quality, packaging, and delivery speed firsthand.",
    tip: "Sample orders ship in 3–7 days. Most factories offer free samples for orders over $200.",
    color: "bg-amber-500",
  },
  {
    num: "03", icon: "ri-message-3-line", title: "Negotiate & RFQ",
    desc: "Send a Request for Quotation (RFQ) directly to the factory. Negotiate pricing, customization, lead times, and payment terms.",
    tip: "Mention your annual volume in the RFQ to unlock better pricing.",
    color: "bg-amber-500",
  },
  {
    num: "04", icon: "ri-stack-line", title: "Place Bulk Order",
    desc: "Once satisfied with the sample, place your bulk order at the discounted factory price. MOQ starts from as low as 5 units.",
    tip: "Bulk orders of 500+ units qualify for free shipping from most Dubai factories.",
    color: "bg-amber-500",
  },
  {
    num: "05", icon: "ri-ship-line", title: "Production & Shipping",
    desc: "The factory produces your order and ships via your preferred method — sea freight, air freight, or express courier.",
    tip: "Track your shipment in real-time through your buyer dashboard.",
    color: "bg-amber-500",
  },
  {
    num: "06", icon: "ri-checkbox-circle-line", title: "Receive & Repeat",
    desc: "Receive your goods, inspect quality, and leave a verified review. Build a long-term relationship with your factory partner.",
    tip: "Repeat buyers get priority production slots and better pricing.",
    color: "bg-amber-500",
  },
];

const dropshipperSteps = [
  {
    num: "01", icon: "ri-store-2-line", title: "Set Up Your Store",
    desc: "Create your online store on any platform (Shopify, WooCommerce, etc.). You don't need any inventory to start.",
    tip: "No upfront investment needed — start dropshipping with $0 inventory.",
    color: "bg-emerald-500",
  },
  {
    num: "02", icon: "ri-filter-3-line", title: "Find Dropship Products",
    desc: "Filter products by 'Dropshipping Available' to see factories that ship directly to your customers. Look for the green badge.",
    tip: "Factories with local warehouses in your target country ship fastest.",
    color: "bg-emerald-500",
  },
  {
    num: "03", icon: "ri-price-tag-3-line", title: "Set Your Retail Price",
    desc: "Buy at factory bulk price, sell at your retail price. Keep the margin. Typical margins range from 30% to 200%.",
    tip: "Start with products that have a 3x markup potential for healthy margins.",
    color: "bg-emerald-500",
  },
  {
    num: "04", icon: "ri-shopping-cart-line", title: "Customer Places Order",
    desc: "When a customer buys from your store, you place the order with the factory using their shipping address.",
    tip: "Many factories support automated order processing via API integration.",
    color: "bg-emerald-500",
  },
  {
    num: "05", icon: "ri-truck-line", title: "Factory Ships Direct",
    desc: "The verified factory ships the product directly to your customer — with your branding if you've arranged private labeling.",
    tip: "Average dropship delivery: 2–5 days from local warehouses, 7–14 days from factory.",
    color: "bg-emerald-500",
  },
  {
    num: "06", icon: "ri-money-dollar-circle-line", title: "Collect Your Profit",
    desc: "You keep the difference between your retail price and the factory price. Scale by adding more products and markets.",
    tip: "Top dropshippers on our platform earn $10,000–$50,000/month.",
    color: "bg-emerald-500",
  },
];

const influencerSteps = [
  {
    num: "01", icon: "ri-user-add-line", title: "Apply to Join",
    desc: "Fill out the influencer application with your social media handles, follower count, and content niche. Approval in 48 hours.",
    tip: "We accept influencers from 1K followers. Micro-influencers often have better conversion rates.",
    color: "bg-amber-600",
  },
  {
    num: "02", icon: "ri-box-3-line", title: "Pick Your Products",
    desc: "Browse verified factory products and select items that match your audience. Request free samples to review before promoting.",
    tip: "Choose products you genuinely love — authentic reviews convert 5x better.",
    color: "bg-amber-600",
  },
  {
    num: "03", icon: "ri-links-line", title: "Get Your Affiliate Link",
    desc: "Receive a unique tracking link for each product. Share it in your posts, stories, videos, and bio.",
    tip: "Add your link to your bio and pin it in comments for maximum visibility.",
    color: "bg-amber-600",
  },
  {
    num: "04", icon: "ri-video-line", title: "Create Content",
    desc: "Make authentic content — unboxings, reviews, tutorials, or lifestyle posts featuring the factory products.",
    tip: "TikTok and YouTube Shorts get 3x more product clicks than static posts.",
    color: "bg-amber-600",
  },
  {
    num: "05", icon: "ri-bar-chart-line", title: "Track Performance",
    desc: "Monitor clicks, conversions, and earnings in real-time through your influencer dashboard.",
    tip: "Check your dashboard weekly to see which products perform best with your audience.",
    color: "bg-amber-600",
  },
  {
    num: "06", icon: "ri-bank-card-line", title: "Earn Commissions",
    desc: "Earn up to 30% commission on every sale. Payments processed monthly via bank transfer, PayPal, or crypto.",
    tip: "Top influencers earn $5,000–$30,000/month promoting verified factory products.",
    color: "bg-amber-600",
  },
];

const stepsMap: Record<TabType, typeof buyerSteps> = {
  buyer: buyerSteps,
  dropshipper: dropshipperSteps,
  influencer: influencerSteps,
};

const faqs = [
  { q: "How do I know a factory is verified?", a: "Verified factories display a gold 'Verified Factory' badge. They've passed our document verification, factory inspection, and quality audit process." },
  { q: "What is the minimum order for a sample?", a: "Most factories accept sample orders from 1 unit. The sample price is higher than bulk price to cover setup costs." },
  { q: "How does dropshipping work with Middle East factories?", a: "Factories with the green 'Dropshipping Available' badge ship directly to your customers. You never touch the inventory — just place the order with the customer's address." },
  { q: "Can I get my own branding on products?", a: "Yes! Many verified factories offer private labeling and OEM services. Mention this in your RFQ or contact the supplier directly." },
  { q: "How long does shipping take?", a: "From local warehouses: 2–5 days. From factory (air freight): 5–10 days. Sea freight bulk orders: 15–30 days depending on destination." },
  { q: "What payment methods are accepted?", a: "We support bank transfer (T/T), Letter of Credit (L/C), PayPal, and escrow payment for buyer protection." },
];

const HowItWorksPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>("buyer");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();
  const steps = stepsMap[activeTab];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="How It Works – Buy from Middle East Factories | SuperMiddleEast"
        description="Learn how to buy direct from verified Middle East factories. Step-by-step guide for bulk buyers, dropshippers and influencers. Sample orders, bulk orders and dropshipping explained."
        keywords="how to buy from factory, Middle East factory guide, dropshipping guide, bulk order process, sample order, influencer program"
        canonical={`${BASE_URL}/how-it-works`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "How It Works – SuperMiddleEast",
            "description": "Step-by-step guide for buying from verified Middle East factories as a bulk buyer, dropshipper or influencer.",
            "url": `${BASE_URL}/how-it-works`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "How It Works", "item": `${BASE_URL}/how-it-works` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I know a factory is verified?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Verified factories display a gold 'Verified Factory' badge. They've passed our document verification, factory inspection, and quality audit process."
                }
              },
              {
                "@type": "Question",
                "name": "What is the minimum order for a sample?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most factories accept sample orders from 1 unit. The sample price is higher than bulk price to cover setup costs."
                }
              },
              {
                "@type": "Question",
                "name": "How does dropshipping work with Middle East factories?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Factories with the green 'Dropshipping Available' badge ship directly to your customers. You never touch the inventory — just place the order with the customer's address."
                }
              },
              {
                "@type": "Question",
                "name": "Can I get my own branding on products?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Many verified factories offer private labeling and OEM services. Mention this in your RFQ or contact the supplier directly."
                }
              },
              {
                "@type": "Question",
                "name": "How long does shipping take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "From local warehouses: 2–5 days. From factory (air freight): 5–10 days. Sea freight bulk orders: 15–30 days depending on destination."
                }
              },
              {
                "@type": "Question",
                "name": "What payment methods are accepted?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We support bank transfer (T/T), Letter of Credit (L/C), PayPal, and escrow payment for buyer protection."
                }
              }
            ]
          }
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-amber-700 to-amber-900 pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 text-center">
          <p className="text-amber-300 text-xs font-semibold uppercase tracking-widest mb-4">Simple & Transparent</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            How It Works
          </h1>
          <p className="text-amber-200 text-lg max-w-2xl mx-auto">
            Whether you're a bulk buyer, dropshipper, or influencer — here's exactly how to start buying from verified Middle East factories.
          </p>
        </div>
      </section>

      {/* Tab Selector */}
      <section className="bg-white border-b border-amber-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-4 flex justify-center gap-3 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? "bg-amber-500 text-white" : "bg-amber-50 border border-amber-200 text-gray-600 hover:border-amber-400 hover:text-amber-600"}`}
            >
              <i className={tab.icon}></i>{tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {activeTab === "buyer" && "How to Buy from Verified Factories"}
              {activeTab === "dropshipper" && "How to Start Dropshipping"}
              {activeTab === "influencer" && "How to Earn as an Influencer"}
            </h2>
            <p className="text-gray-500 mt-2 text-base">
              {activeTab === "buyer" && "From discovery to delivery — your complete buying guide"}
              {activeTab === "dropshipper" && "Sell without inventory — ship directly from factory to customer"}
              {activeTab === "influencer" && "Promote verified products and earn commissions on every sale"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={step.num} className="bg-white rounded-2xl p-6 border border-amber-100 hover:-translate-y-1 transition-transform duration-300 relative">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 flex items-center justify-center ${step.color} rounded-xl flex-shrink-0`}>
                    <i className={`${step.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-gray-300">STEP {step.num}</span>
                    <h3 className="font-bold text-gray-900 text-base">{step.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <div className="flex items-start gap-2">
                    <i className="ri-lightbulb-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
                    <p className="text-amber-700 text-xs leading-relaxed">{step.tip}</p>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center bg-amber-200 rounded-full z-10">
                    <i className="ri-arrow-right-s-line text-amber-600 text-sm"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            {activeTab === "buyer" && (
              <button onClick={() => navigate("/products")} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                <i className="ri-search-line"></i> Start Browsing Products
              </button>
            )}
            {activeTab === "dropshipper" && (
              <button onClick={() => navigate("/products?dropshipping=true")} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                <i className="ri-truck-line"></i> Browse Dropship Products
              </button>
            )}
            {activeTab === "influencer" && (
              <button onClick={() => navigate("/influencer-program")} className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                <i className="ri-star-line"></i> Apply to Influencer Program
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Order Types Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2">Order Types</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Sample vs Bulk vs Dropship</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                type: "Sample Order", icon: "ri-test-tube-line", color: "bg-amber-500", textColor: "text-amber-600", bgLight: "bg-amber-50",
                points: ["1 unit minimum", "Higher unit price", "3–7 day delivery", "Quality inspection", "No commitment required", "Perfect for testing"],
              },
              {
                type: "Bulk Order", icon: "ri-stack-line", color: "bg-emerald-600", textColor: "text-emerald-600", bgLight: "bg-emerald-50",
                points: ["MOQ from 5 units", "Lowest factory price", "7–21 day production", "Custom branding available", "Volume discounts", "Best for resellers"],
              },
              {
                type: "Dropship Order", icon: "ri-truck-line", color: "bg-amber-700", textColor: "text-amber-700", bgLight: "bg-amber-50",
                points: ["1 unit per customer", "No inventory needed", "2–14 day delivery", "Ships to customer direct", "Your branding option", "Best for online stores"],
              },
            ].map((o) => (
              <div key={o.type} className={`${o.bgLight} rounded-2xl p-6 border border-amber-100`}>
                <div className={`w-12 h-12 flex items-center justify-center ${o.color} rounded-xl mb-4`}>
                  <i className={`${o.icon} text-white text-xl`}></i>
                </div>
                <h3 className={`font-bold text-lg mb-4 ${o.textColor}`}>{o.type}</h3>
                <ul className="space-y-2">
                  {o.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-amber-500 flex-shrink-0"></i>{p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2">Common Questions</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-amber-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-amber-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  <i className={`${openFaq === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-amber-500 text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-amber-800">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to Get Started?</h2>
          <p className="text-amber-200 text-base mb-8 max-w-xl mx-auto">Join thousands of buyers, dropshippers, and influencers already working with verified Middle East factories.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/products")} className="bg-white text-amber-700 hover:bg-amber-50 font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line mr-2"></i>Browse Products
            </button>
            <button onClick={() => navigate("/register-supplier")} className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-building-4-line mr-2"></i>Register as Supplier
            </button>
            <button onClick={() => navigate("/influencer-program")} className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-4 rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-star-line mr-2"></i>Join Influencer Program
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
